import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Avatar,
  LinearProgress,
} from "@mui/material";
import {
  TrendingUp,
  People,
  AccountBalance,
  LocalHospital,
  Warning,
  CheckCircle,
  Schedule,
  AttachMoney,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import { useTranslation } from "react-i18next";

const Dashboard: React.FC = () => {
  const { t } = useTranslation();

  // Mock data for charts
  const revenueData = [
    { month: "Jan", revenue: 4000, expenses: 2400 },
    { month: "Feb", revenue: 3000, expenses: 1398 },
    { month: "Mar", revenue: 2000, expenses: 9800 },
    { month: "Apr", revenue: 2780, expenses: 3908 },
    { month: "May", revenue: 1890, expenses: 4800 },
    { month: "Jun", revenue: 2390, expenses: 3800 },
  ];

  const patientData = [
    { name: "Outpatient", value: 400, color: "#0088FE" },
    { name: "Inpatient", value: 300, color: "#00C49F" },
    { name: "Emergency", value: 200, color: "#FFBB28" },
    { name: "Surgery", value: 100, color: "#FF8042" },
  ];

  const departmentData = [
    { department: "Cardiology", patients: 120, revenue: 45000 },
    { department: "Orthopedics", patients: 95, revenue: 38000 },
    { department: "Pediatrics", patients: 150, revenue: 32000 },
    { department: "Neurology", patients: 80, revenue: 28000 },
    { department: "Emergency", patients: 200, revenue: 55000 },
  ];

  const recentActivities = [
    {
      action: "New patient registered",
      time: "2 minutes ago",
      type: "success",
    },
    { action: "Payment received", time: "15 minutes ago", type: "success" },
    { action: "Inventory low alert", time: "1 hour ago", type: "warning" },
    { action: "Staff shift completed", time: "2 hours ago", type: "info" },
    {
      action: "Equipment maintenance due",
      time: "3 hours ago",
      type: "warning",
    },
  ];

  const kpiCards = [
    {
      title: "Total Revenue",
      value: "SAR 2,450,000",
      change: "+12.5%",
      icon: <AttachMoney />,
      color: "#4caf50",
    },
    {
      title: "Active Patients",
      value: "1,234",
      change: "+8.2%",
      icon: <People />,
      color: "#2196f3",
    },
    {
      title: "Staff Count",
      value: "456",
      change: "+3.1%",
      icon: <People />,
      color: "#ff9800",
    },
    {
      title: "Occupancy Rate",
      value: "87%",
      change: "+2.3%",
      icon: <LocalHospital />,
      color: "#9c27b0",
    },
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "success":
        return <CheckCircle color="success" />;
      case "warning":
        return <Warning color="warning" />;
      default:
        return <Schedule color="info" />;
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: "linear-gradient(135deg, #2E7D8A 0%, #5BA3B0 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 1,
          }}
        >
          {t("dashboard.title")}
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ fontWeight: 400 }}
        >
          {t("dashboard.welcome")}
        </Typography>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4, width: "100%" }}>
        {kpiCards.map((kpi, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: "100%",
                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                border: "1px solid rgba(46, 125, 138, 0.1)",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0px 12px 28px rgba(46, 125, 138, 0.15)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: kpi.color,
                      mr: 2,
                      width: 48,
                      height: 48,
                      boxShadow: `0px 4px 12px ${kpi.color}40`,
                    }}
                  >
                    {kpi.icon}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: "text.primary" }}
                    >
                      {kpi.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      {kpi.title}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    background: "rgba(76, 175, 80, 0.1)",
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    mt: 2,
                  }}
                >
                  <TrendingUp
                    sx={{ color: "success.main", mr: 1, fontSize: 20 }}
                  />
                  <Typography
                    variant="body2"
                    color="success.main"
                    sx={{ fontWeight: 600 }}
                  >
                    {kpi.change}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 4, width: "100%" }}>
        {/* Revenue Chart */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              border: "1px solid rgba(46, 125, 138, 0.1)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                  {t("dashboard.revenueVsExpenses")}
                </Typography>
                <Chip
                  label="Last 6 Months"
                  size="small"
                  sx={{
                    background: "rgba(46, 125, 138, 0.1)",
                    color: "primary.main",
                    fontWeight: 500,
                  }}
                />
              </Box>
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient
                      id="revenueGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#2E7D8A" stopOpacity={0.3} />
                      <stop
                        offset="95%"
                        stopColor="#2E7D8A"
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                    <linearGradient
                      id="expenseGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#E91E63" stopOpacity={0.3} />
                      <stop
                        offset="95%"
                        stopColor="#E91E63"
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12 }}
                    axisLine={{ stroke: "#e0e0e0" }}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    axisLine={{ stroke: "#e0e0e0" }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "white",
                      border: "1px solid #e0e0e0",
                      borderRadius: 8,
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stackId="1"
                    stroke="#2E7D8A"
                    fill="url(#revenueGradient)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="expenses"
                    stackId="1"
                    stroke="#E91E63"
                    fill="url(#expenseGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Patient Distribution */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              border: "1px solid rgba(46, 125, 138, 0.1)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                {t("dashboard.patientDistribution")}
              </Typography>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={patientData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={90}
                    fill="#2E7D8A"
                    dataKey="value"
                    stroke="#ffffff"
                    strokeWidth={2}
                  >
                    {patientData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "white",
                      border: "1px solid #e0e0e0",
                      borderRadius: 8,
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Department Performance and Recent Activity */}
      <Grid container spacing={3} sx={{ mb: 4, width: "100%" }}>
        {/* Department Performance */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              border: "1px solid rgba(46, 125, 138, 0.1)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                {t("dashboard.departmentPerformance")}
              </Typography>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={departmentData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="department"
                    tick={{ fontSize: 12 }}
                    axisLine={{ stroke: "#e0e0e0" }}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    axisLine={{ stroke: "#e0e0e0" }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "white",
                      border: "1px solid #e0e0e0",
                      borderRadius: 8,
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                  <Bar
                    dataKey="patients"
                    fill="#2E7D8A"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar dataKey="revenue" fill="#E91E63" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              border: "1px solid rgba(46, 125, 138, 0.1)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                {t("dashboard.recentActivity")}
              </Typography>
              <List sx={{ p: 0 }}>
                {recentActivities.map((activity, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      px: 0,
                      py: 1.5,
                      borderRadius: 2,
                      mb: 1,
                      background: "rgba(46, 125, 138, 0.02)",
                      "&:hover": {
                        background: "rgba(46, 125, 138, 0.05)",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getActivityIcon(activity.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {activity.action}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {activity.time}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Status */}
      <Grid container spacing={3} sx={{ width: "100%" }}>
        <Grid item xs={12}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              border: "1px solid rgba(46, 125, 138, 0.1)",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                {t("dashboard.systemStatus")}
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background: "rgba(46, 125, 138, 0.05)",
                      border: "1px solid rgba(46, 125, 138, 0.1)",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, mb: 2, color: "text.primary" }}
                    >
                      Database Performance
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={85}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        background: "rgba(46, 125, 138, 0.1)",
                        "& .MuiLinearProgress-bar": {
                          background:
                            "linear-gradient(90deg, #2E7D8A 0%, #5BA3B0 100%)",
                          borderRadius: 4,
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="success.main"
                      sx={{ fontWeight: 500, mt: 1, display: "block" }}
                    >
                      85% - Excellent
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background: "rgba(76, 175, 80, 0.05)",
                      border: "1px solid rgba(76, 175, 80, 0.1)",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, mb: 2, color: "text.primary" }}
                    >
                      Server Uptime
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={99}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        background: "rgba(76, 175, 80, 0.1)",
                        "& .MuiLinearProgress-bar": {
                          background:
                            "linear-gradient(90deg, #4CAF50 0%, #81C784 100%)",
                          borderRadius: 4,
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="success.main"
                      sx={{ fontWeight: 500, mt: 1, display: "block" }}
                    >
                      99% - Excellent
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background: "rgba(33, 150, 243, 0.05)",
                      border: "1px solid rgba(33, 150, 243, 0.1)",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, mb: 2, color: "text.primary" }}
                    >
                      Security Status
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={95}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        background: "rgba(33, 150, 243, 0.1)",
                        "& .MuiLinearProgress-bar": {
                          background:
                            "linear-gradient(90deg, #2196F3 0%, #64B5F6 100%)",
                          borderRadius: 4,
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="info.main"
                      sx={{ fontWeight: 500, mt: 1, display: "block" }}
                    >
                      95% - Secure
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background: "rgba(76, 175, 80, 0.05)",
                      border: "1px solid rgba(76, 175, 80, 0.1)",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, mb: 2, color: "text.primary" }}
                    >
                      Backup Status
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={100}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        background: "rgba(76, 175, 80, 0.1)",
                        "& .MuiLinearProgress-bar": {
                          background:
                            "linear-gradient(90deg, #4CAF50 0%, #81C784 100%)",
                          borderRadius: 4,
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="success.main"
                      sx={{ fontWeight: 500, mt: 1, display: "block" }}
                    >
                      100% - Complete
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
